import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Trash2, Clock, Euro, Users, Calendar, Umbrella } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const SERVICE_TYPES = [
  { value: "appointment", label: "Appuntamento", icon: Clock, description: "Servizio classico 1:1 (es. taglio capelli)" },
  { value: "event", label: "Evento/Attività", icon: Users, description: "Multi-posto (es. corso, escursione)" },
  { value: "daily_rental", label: "Noleggio Giornaliero", icon: Umbrella, description: "Prenotazione multi-giorno (es. ombrellone)" }
];

const Services = () => {
  const { user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration_minutes: 60,
    price: 0,
    currency: "EUR",
    max_capacity: 1,
    is_active: true,
    color: "#4F46E5",
    service_type: "appointment",
    min_seats: 1,
    max_seats_per_booking: 10,
    price_per_day: 0,
    min_days: 1,
    max_days: 30
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);

        const servicesResponse = await axios.get(`${API}/businesses/${biz.business_id}/services`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setServices(servicesResponse.data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei servizi");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingService(null);
    setFormData({
      name: "",
      description: "",
      duration_minutes: 60,
      price: 0,
      currency: "EUR",
      max_capacity: 1,
      is_active: true,
      color: "#4F46E5",
      service_type: "appointment",
      min_seats: 1,
      max_seats_per_booking: 10,
      price_per_day: 0,
      min_days: 1,
      max_days: 30
    });
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || "",
      duration_minutes: service.duration_minutes,
      price: service.price,
      currency: service.currency,
      max_capacity: service.max_capacity,
      is_active: service.is_active,
      color: service.color,
      service_type: service.service_type || "appointment",
      min_seats: service.min_seats || 1,
      max_seats_per_booking: service.max_seats_per_booking || 10,
      price_per_day: service.price_per_day || 0,
      min_days: service.min_days || 1,
      max_days: service.max_days || 30
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.service_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Servizio aggiornato!");
      } else {
        await axios.post(`${API}/businesses/${business.business_id}/services`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Servizio creato!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!confirm("Sei sicuro di voler eliminare questo servizio?")) return;

    try {
      await axios.delete(`${API}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Servizio eliminato!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const colors = [
    "#4F46E5", "#7C3AED", "#DB2777", "#DC2626",
    "#EA580C", "#CA8A04", "#16A34A", "#0891B2"
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Crea prima la tua attività dalla Dashboard</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="services-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Servizi
            </h1>
            <p className="text-slate-500 mt-1">Gestisci i servizi della tua attività</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="add-service-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Servizio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingService ? "Modifica Servizio" : "Nuovo Servizio"}
                </DialogTitle>
                <DialogDescription>
                  {editingService ? "Modifica i dettagli del servizio" : "Aggiungi un nuovo servizio alla tua attività"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Service Type Selection */}
                <div className="space-y-2">
                  <Label>Tipo di Servizio *</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {SERVICE_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <div
                          key={type.value}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.service_type === type.value
                              ? 'border-primary bg-primary/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setFormData({ ...formData, service_type: type.value })}
                          data-testid={`service-type-${type.value}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${formData.service_type === type.value ? 'bg-primary/10' : 'bg-slate-100'}`}>
                              <Icon className={`h-4 w-4 ${formData.service_type === type.value ? 'text-primary' : 'text-slate-500'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{type.label}</p>
                              <p className="text-xs text-slate-500">{type.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Nome Servizio *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={formData.service_type === "event" ? "Es. Escursione in barca" : formData.service_type === "daily_rental" ? "Es. Ombrellone Prima Fila" : "Es. Consulenza 60 minuti"}
                    required
                    data-testid="service-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrivi il servizio..."
                    rows={2}
                    data-testid="service-description-input"
                  />
                </div>

                {/* Conditional fields based on service type */}
                {formData.service_type === "appointment" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="duration">Durata (minuti)</Label>
                        <Input
                          id="duration"
                          type="number"
                          min="5"
                          value={formData.duration_minutes}
                          onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                          data-testid="service-duration-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="price">Prezzo (€)</Label>
                        <Input
                          id="price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                          data-testid="service-price-input"
                        />
                      </div>
                    </div>
                  </>
                )}

                {formData.service_type === "event" && (
                  <>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Evento/Attività:</strong> I clienti potranno prenotare uno o più posti per questo evento.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="duration">Durata (minuti)</Label>
                        <Input
                          id="duration"
                          type="number"
                          min="5"
                          value={formData.duration_minutes}
                          onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="price">Prezzo per Posto (€)</Label>
                        <Input
                          id="price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="max_capacity">Posti Totali</Label>
                        <Input
                          id="max_capacity"
                          type="number"
                          min="1"
                          value={formData.max_capacity}
                          onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="min_seats">Min Posti</Label>
                        <Input
                          id="min_seats"
                          type="number"
                          min="1"
                          value={formData.min_seats}
                          onChange={(e) => setFormData({ ...formData, min_seats: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="max_seats">Max Posti/Pren.</Label>
                        <Input
                          id="max_seats"
                          type="number"
                          min="1"
                          value={formData.max_seats_per_booking}
                          onChange={(e) => setFormData({ ...formData, max_seats_per_booking: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  </>
                )}

                {formData.service_type === "daily_rental" && (
                  <>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>Noleggio Giornaliero:</strong> I clienti selezioneranno un intervallo di date. Il prezzo sarà calcolato per giorno.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price_per_day">Prezzo al Giorno (€)</Label>
                        <Input
                          id="price_per_day"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.price_per_day}
                          onChange={(e) => setFormData({ ...formData, price_per_day: parseFloat(e.target.value), price: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="max_capacity">Quantità Disponibili</Label>
                        <Input
                          id="max_capacity"
                          type="number"
                          min="1"
                          value={formData.max_capacity}
                          onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
                        />
                        <p className="text-xs text-slate-500">Es. numero di ombrelloni</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="min_days">Minimo Giorni</Label>
                        <Input
                          id="min_days"
                          type="number"
                          min="1"
                          value={formData.min_days}
                          onChange={(e) => setFormData({ ...formData, min_days: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="max_days">Massimo Giorni</Label>
                        <Input
                          id="max_days"
                          type="number"
                          min="1"
                          value={formData.max_days}
                          onChange={(e) => setFormData({ ...formData, max_days: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Colore</Label>
                  <div className="flex gap-2">
                    {colors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full transition-all ${formData.color === color ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Attivo</Label>
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="service-active-switch"
                  />
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-service-btn">
                    {saving ? "Salvataggio..." : editingService ? "Salva Modifiche" : "Crea Servizio"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Services Grid */}
        {services.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <p className="text-slate-500 mb-4">Nessun servizio ancora creato</p>
              <Button onClick={openCreateDialog} data-testid="empty-add-service-btn">
                <Plus className="h-4 w-4 mr-2" />
                Crea il tuo primo servizio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {services.map((service) => {
              const serviceType = SERVICE_TYPES.find(t => t.value === (service.service_type || "appointment"));
              const TypeIcon = serviceType?.icon || Clock;
              
              return (
                <Card key={service.service_id} className="border-slate-100 shadow-sm card-hover" data-testid={`service-card-${service.service_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-12 rounded-full"
                          style={{ backgroundColor: service.color }}
                        />
                        <div>
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {service.name}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <TypeIcon className="h-3 w-3" />
                              {serviceType?.label || "Appuntamento"}
                            </Badge>
                            {!service.is_active && (
                              <Badge variant="secondary" className="text-xs">
                                Disattivato
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`service-menu-${service.service_id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(service)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(service.service_id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {service.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{service.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      {service.service_type === "daily_rental" ? (
                        <>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Euro className="h-4 w-4" />
                            <span>€{(service.price_per_day || service.price)?.toFixed(2)}/giorno</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Calendar className="h-4 w-4" />
                            <span>{service.min_days || 1}-{service.max_days || 30} giorni</span>
                          </div>
                        </>
                      ) : service.service_type === "event" ? (
                        <>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Euro className="h-4 w-4" />
                            <span>€{service.price?.toFixed(2)}/posto</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Users className="h-4 w-4" />
                            <span>{service.max_capacity} posti</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Clock className="h-4 w-4" />
                            <span>{service.duration_minutes} min</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Clock className="h-4 w-4" />
                            <span>{service.duration_minutes} min</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Euro className="h-4 w-4" />
                            <span>{service.price > 0 ? `€${service.price.toFixed(2)}` : "Gratuito"}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Services;
